import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Cpu,
  Cloud,
  Code2,
  Users,
  GitBranch,
  Database,
  ArrowUpRight,
} from "lucide-react";

export const SERVICES = [
  {
    id: "modernization-assurance",
    name: "Modernization Assurance",
    blurb: "Independent, verification-first layer that mathematically proves behavior is preserved during legacy modernization.",
    icon: ShieldCheck,
    accent: "green",
    tags: ["Parallel run", "Behavior diff", "Observability"],
    size: "lg",
  },
  {
    id: "full-modernization",
    name: "Full Modernization",
    blurb: "End-to-end modernization of IBM-era stacks (Natural/Adabas, COBOL, mainframe) with verification built in.",
    icon: Cpu,
    accent: "cyan",
    tags: ["Milestone-driven", "Production-ready"],
    size: "md",
  },
  {
    id: "application-development",
    name: "Application Development",
    blurb: "Custom web, mobile & backend engineering. We ship production software like your best in-house team.",
    icon: Code2,
    accent: "green",
    tags: ["Web", "Mobile", "APIs", "SaaS"],
    size: "md",
  },
  {
    id: "cloud",
    name: "Cloud Services",
    blurb: "AWS, Azure & GCP: cloud migration, infrastructure-as-code, managed cost & security operations.",
    icon: Cloud,
    accent: "cyan",
    tags: ["AWS", "GCP", "Azure", "IaC"],
    size: "md",
  },
  {
    id: "consulting",
    name: "IT Consulting & Staffing",
    blurb: "Expert consultants and contract engineers embedded in your teams, hourly, project, or long-term.",
    icon: Users,
    accent: "green",
    tags: ["Contractors", "Consultants", "Team-leads"],
    size: "lg",
  },
  {
    id: "devops",
    name: "DevOps & Automation",
    blurb: "CI/CD pipelines, platform engineering, SRE on-call, and automation that removes toil permanently.",
    icon: GitBranch,
    accent: "cyan",
    tags: ["K8s", "CI/CD", "SRE"],
    size: "md",
  },
  {
    id: "data-ai",
    name: "Data & AI Engineering",
    blurb: "Data pipelines, LLM integrations, AI agents, and governance systems that scale safely inside enterprises.",
    icon: Database,
    accent: "green",
    tags: ["LLMs", "Agents", "Pipelines"],
    size: "md",
  },
];

const accentMap = {
  green: {
    border: "hover:border-neon-green",
    text: "text-neon-green",
    shadow: "hover:shadow-[0_0_40px_rgba(0,255,65,0.2)]",
    bar: "bg-neon-green",
  },
  cyan: {
    border: "hover:border-neon-cyan",
    text: "text-neon-cyan",
    shadow: "hover:shadow-[0_0_40px_rgba(0,255,255,0.2)]",
    bar: "bg-neon-cyan",
  },
};

export default function ServicesGrid({ compact = false }) {
  return (
    <section
      data-testid="services-grid"
      id="services"
      className="relative border-b border-white/10 bg-obsidian"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24 lg:py-32">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
          <div>
            <div className="chip text-neon-green">&gt; /services</div>
            <h2 className="mt-3 font-display font-black text-4xl md:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
              IT services engineered for <br className="hidden md:block" />
              <span className="text-neon-cyan">serious</span> enterprises.
            </h2>
          </div>
          <p className="max-w-md text-white/60 text-sm leading-relaxed">
            Hire us as your modernization partner, your development team, your
            on-demand consultancy, or all of the above. One accountable
            engineering partner, end-to-end.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SERVICES.map((s, idx) => {
            const a = accentMap[s.accent];
            const isWide = idx === 0;
            return (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.5, delay: idx * 0.05 }}
                className={`group relative border border-white/10 bg-carbon p-6 lg:p-8 transition-all duration-300 ${a.border} ${a.shadow} ${
                  isWide ? "md:col-span-2 lg:col-span-2" : ""
                }`}
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className={`h-10 w-10 border border-white/10 bg-obsidian inline-flex items-center justify-center ${a.text}`}>
                    <s.icon size={18} />
                  </div>
                  <span className="chip text-white/40">[0{idx + 1}]</span>
                </div>

                <h3 className="mt-6 font-display font-bold text-white text-2xl leading-tight">
                  {s.name}
                </h3>
                <p className="mt-3 text-sm text-white/60 leading-relaxed max-w-xl">
                  {s.blurb}
                </p>

                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="px-2 py-1 border border-white/10 text-[11px] font-mono text-white/50"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <Link
                  to={`/services#${s.id}`}
                  className={`mt-6 inline-flex items-center gap-2 text-sm font-mono ${a.text} opacity-80 group-hover:opacity-100`}
                  data-testid={`service-link-${s.id}`}
                >
                  Explore <ArrowUpRight size={14} />
                </Link>

                <div className={`absolute left-0 top-0 h-full w-[1px] ${a.bar} opacity-0 group-hover:opacity-70 transition-opacity`} />
              </motion.div>
            );
          })}
        </div>

        {compact && (
          <div className="mt-12 text-center">
            <Link
              to="/services"
              data-testid="services-view-all"
              className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 text-sm font-mono uppercase tracking-wider hover:border-neon-green hover:text-neon-green transition-colors"
            >
              View all services <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
